
import React, { useState, useRef, useEffect } from 'react';
import {
  Layout, Form, Input, Button, Select, ColorPicker, InputNumber,
  Card, Divider, Space, Tag, message, Typography, Upload, Switch,
  Tabs, Modal, Radio, Tooltip
} from 'antd';
import { saveTicketTemplateApi } from '../../../redux/api/api';
import {
  EyeOutlined, SaveOutlined, DeleteOutlined, PictureOutlined,
  QrcodeOutlined, FontSizeOutlined, DownloadOutlined,
  BarcodeOutlined, BorderOutlined, CalendarOutlined,
  MinusOutlined, UploadOutlined, MenuOutlined, LockOutlined,
  UnlockOutlined, EyeInvisibleOutlined, AlignCenterOutlined,
  AlignLeftOutlined, AlignRightOutlined, CopyOutlined
} from '@ant-design/icons';
// import BadgeTemplatesGallery from './BadgeTemplatesGallery';

const { Header, Content, Sider } = Layout;
const { Option } = Select;
const { Title, Text } = Typography;
const { TabPane } = Tabs;

// Field Types Configuration
const fieldTypes = [
  {
    type: 'text',
    label: 'Text Field',
    icon: <FontSizeOutlined style={{ fontSize: 18 }} />,
    defaultProperties: {
      fontSize: 16,
      fontWeight: 'normal',
      color: '#000000',
      content: 'Sample Text',
      alignment: 'left'
    }
  },
  {
    type: 'qrcode',
    label: 'QR Code',
    icon: <QrcodeOutlined style={{ fontSize: 18 }} />,
    defaultProperties: {
      size: 100,
      content: 'QR_CODE_DATA',
      qrColor: '#000000',
      bgColor: '#FFFFFF'
    }
  },
  {
    type: 'barcode',
    label: 'Barcode',
    icon: <BarcodeOutlined style={{ fontSize: 18 }} />,
    defaultProperties: {
      width: 200,
      height: 50,
      content: 'BARCODE12345',
      color: '#000000',
      format: 'CODE128'
    }
  },
  {
    type: 'divider',
    label: 'Divider Line',
    icon: <MinusOutlined style={{ fontSize: 18 }} />,
    defaultProperties: {
      width: 300,
      height: 2,
      color: '#000000',
      style: 'solid'
    }
  },
  {
    type: 'image',
    label: 'Image/Logo',
    icon: <PictureOutlined style={{ fontSize: 18 }} />,
    defaultProperties: {
      width: 80,
      height: 80,
      content: '',
      imageData: null,
      objectFit: 'cover',
      opacity: 1
    }
  },
  {
    type: 'datetime',
    label: 'Date/Time',
    icon: <CalendarOutlined style={{ fontSize: 18 }} />,
    defaultProperties: {
      fontSize: 14,
      format: 'MMMM DD, YYYY',
      content: 'November 21, 2025',
      color: '#000000'
    }
  },
  {
    type: 'container',
    label: 'Design Box/Section',
    icon: <BorderOutlined style={{ fontSize: 18 }} />,
    defaultProperties: {
      width: 350,
      height: 100,
      backgroundColor: '#E3F2FD',
      borderRadius: 8,
      borderStyle: 'solid',
      borderColor: '#2196F3',
      borderWidth: 2,
      opacity: 1
    }
  }
];

// Preset Templates
const presetTemplates = {
  blank: {
    name: 'Blank Template',
    backgroundColor: '#FFFFFF',
    size: { width: 400, height: 600 },
    fields: []
  },
  conference: {
    name: 'Conference Badge',
    backgroundColor: '#1976D2',
    size: { width: 400, height: 600 },
    fields: [
      {
        id: 'container_1',
        type: 'container',
        label: 'Header Section',
        properties: {
          width: 400,
          height: 120,
          backgroundColor: '#0D47A1',
          borderRadius: 0,
          borderStyle: 'none',
          borderColor: 'transparent',
          borderWidth: 0,
          opacity: 1
        },
        position: { x: 0, y: 0 },
        locked: false,
        visible: true,
        zIndex: 0
      },
      {
        id: 'text_1',
        type: 'text',
        label: 'Event Name',
        properties: {
          fontSize: 24,
          color: '#FFFFFF',
          content: 'Tech Conference 2025',
          fontWeight: 'bold',
          alignment: 'center'
        },
        position: { x: 80, y: 40 },
        locked: false,
        visible: true,
        zIndex: 1
      },
      {
        id: 'image_1',
        type: 'image',
        label: 'Attendee Photo',
        properties: {
          width: 120,
          height: 120,
          imageData: null,
          objectFit: 'cover',
          opacity: 1
        },
        position: { x: 140, y: 150 },
        locked: false,
        visible: true,
        zIndex: 1
      },
      {
        id: 'text_2',
        type: 'text',
        label: 'Attendee Name',
        properties: {
          fontSize: 20,
          color: '#FFFFFF',
          content: 'John Doe',
          fontWeight: 'bold',
          alignment: 'center'
        },
        position: { x: 110, y: 290 },
        locked: false,
        visible: true,
        zIndex: 1
      },
      {
        id: 'text_3',
        type: 'text',
        label: 'Company',
        properties: {
          fontSize: 14,
          color: '#E3F2FD',
          content: 'Tech Corp Inc.',
          fontWeight: 'normal',
          alignment: 'center'
        },
        position: { x: 130, y: 325 },
        locked: false,
        visible: true,
        zIndex: 1
      },
      {
        id: 'qrcode_1',
        type: 'qrcode',
        label: 'QR Code',
        properties: {
          size: 100,
          content: 'ATTENDEE_QR_123456',
          qrColor: '#000000',
          bgColor: '#FFFFFF'
        },
        position: { x: 150, y: 420 },
        locked: false,
        visible: true,
        zIndex: 1
      }
    ]
  },
  staff: {
    name: 'Staff Badge',
    backgroundColor: '#2E7D32',
    size: { width: 300, height: 400 },
    fields: [
      {
        id: 'container_1',
        type: 'container',
        label: 'Staff Header',
        properties: {
          width: 300,
          height: 80,
          backgroundColor: '#1B5E20',
          borderRadius: 0,
          borderStyle: 'none',
          borderColor: 'transparent',
          borderWidth: 0,
          opacity: 1
        },
        position: { x: 0, y: 0 },
        locked: false,
        visible: true,
        zIndex: 0
      },
      {
        id: 'text_1',
        type: 'text',
        label: 'Staff Label',
        properties: {
          fontSize: 18,
          color: '#FFFFFF',
          content: 'STAFF',
          fontWeight: 'bold',
          alignment: 'center'
        },
        position: { x: 120, y: 30 },
        locked: false,
        visible: true,
        zIndex: 1
      },
      {
        id: 'image_1',
        type: 'image',
        label: 'Staff Photo',
        properties: {
          width: 100,
          height: 100,
          imageData: null,
          objectFit: 'cover',
          opacity: 1
        },
        position: { x: 100, y: 100 },
        locked: false,
        visible: true,
        zIndex: 1
      },
      {
        id: 'text_2',
        type: 'text',
        label: 'Staff Name',
        properties: {
          fontSize: 16,
          color: '#FFFFFF',
          content: 'Alex Johnson',
          fontWeight: 'bold',
          alignment: 'center'
        },
        position: { x: 80, y: 220 },
        locked: false,
        visible: true,
        zIndex: 1
      },
      {
        id: 'qrcode_1',
        type: 'qrcode',
        label: 'QR Code',
        properties: {
          size: 80,
          content: 'STAFF_QR_789',
          qrColor: '#000000',
          bgColor: '#FFFFFF'
        },
        position: { x: 110, y: 280 },
        locked: false,
        visible: true,
        zIndex: 1
      }
    ]
  },
  vip: {
    name: 'VIP Pass',
    backgroundColor: '#000000',
    size: { width: 400, height: 600 },
    fields: [
      {
        id: 'container_1',
        type: 'container',
        label: 'Gold Section',
        properties: {
          width: 400,
          height: 150,
          backgroundColor: '#FFD700',
          borderRadius: 0,
          borderStyle: 'none',
          borderColor: 'transparent',
          borderWidth: 0,
          opacity: 1
        },
        position: { x: 0, y: 0 },
        locked: false,
        visible: true,
        zIndex: 0
      },
      {
        id: 'text_1',
        type: 'text',
        label: 'VIP Label',
        properties: {
          fontSize: 36,
          color: '#000000',
          content: 'VIP ACCESS',
          fontWeight: 'bold',
          alignment: 'center'
        },
        position: { x: 90, y: 60 },
        locked: false,
        visible: true,
        zIndex: 1
      },
      {
        id: 'text_2',
        type: 'text',
        label: 'VIP Name',
        properties: {
          fontSize: 22,
          color: '#FFD700',
          content: 'Distinguished Guest',
          fontWeight: 'bold',
          alignment: 'center'
        },
        position: { x: 70, y: 200 },
        locked: false,
        visible: true,
        zIndex: 1
      },
      {
        id: 'barcode_1',
        type: 'barcode',
        label: 'Barcode',
        properties: {
          width: 250,
          height: 60,
          content: 'VIP987654321',
          color: '#FFD700',
          format: 'CODE128'
        },
        position: { x: 75, y: 450 },
        locked: false,
        visible: true,
        zIndex: 1
      }
    ]
  }
};

// Size presets
const sizeConfigs = {
  'standard': { width: 400, height: 600 },
  'badge': { width: 300, height: 400 },
  'landscape': { width: 600, height: 400 },
  'a4-portrait': { width: 210, height: 297 },
  'custom': null
};

const TemplateDesigner = ({ selectedTemplate, onClose, onSave }) => {
  const [templateName, setTemplateName] = useState('Blank Template');
  const [backgroundColor, setBackgroundColor] = useState('#FFFFFF');
  const [size, setSize] = useState('standard');
  const [customWidth, setCustomWidth] = useState(400);
  const [customHeight, setCustomHeight] = useState(600);

  // Front and Back sides
  const [activeSide, setActiveSide] = useState('front');
  const [frontFields, setFrontFields] = useState([]);
  const [backFields, setBackFields] = useState([]);

  const [selectedField, setSelectedField] = useState(null);
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [gridSnap, setGridSnap] = useState(true);
  const [showGrid, setShowGrid] = useState(true);

  const canvasRef = useRef(null);

  useEffect(() => {
    if (selectedTemplate) {
      //console.log('Loading selected template:', selectedTemplate);

      // Set basic template properties
      setTemplateName(selectedTemplate.name || 'Untitled Template');
      setBackgroundColor(selectedTemplate.backgroundColor || '#FFFFFF');

      // Handle size configuration
      if (selectedTemplate.size) {
        // If size is a string preset
        if (typeof selectedTemplate.size === 'string') {
          setSize(selectedTemplate.size);
        } else {
          // If size is an object with width/height
          setSize('custom');
          setCustomWidth(selectedTemplate.size.width || 400);
          setCustomHeight(selectedTemplate.size.height || 600);
        }
      }

      // Load fields - handle both frontFields and fields for backward compatibility
      if (selectedTemplate.frontFields && selectedTemplate.frontFields.length > 0) {
        setFrontFields(selectedTemplate.frontFields);
      } else if (selectedTemplate.fields && selectedTemplate.fields.length > 0) {
        // Convert from backend format to frontend format
        const convertedFields = selectedTemplate.fields.map(field => ({
          id: field._id || `${field.type}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          type: field.type === 'qr_code' ? 'qrcode' : field.type, // Convert qr_code to qrcode
          label: field.name || field.label || 'Field',
          properties: {
            fontSize: field.style?.fontSize || 16,
            fontWeight: field.style?.fontWeight || 'normal',
            color: field.style?.color || '#000000',
            content: field.content || 'Sample Text',
            alignment: field.style?.alignment || 'left',
            // Add other properties based on field type
            ...(field.type === 'qr_code' && {
              size: field.position?.width || 100,
              qrColor: '#000000',
              bgColor: '#FFFFFF'
            }),
            ...(field.type === 'image' && {
              width: field.position?.width || 80,
              height: field.position?.height || 80
            }),
            // Add other field type conversions as needed
          },
          position: {
            x: field.position?.x || 0,
            y: field.position?.y || 0
          },
          locked: field.locked || false,
          visible: field.visible !== false,
          zIndex: field.zIndex || 0
        }));
        setFrontFields(convertedFields);
      }

      // Load back fields if they exist
      if (selectedTemplate.backFields && selectedTemplate.backFields.length > 0) {
        setBackFields(selectedTemplate.backFields);
      }

      message.success(`Editing: ${selectedTemplate.name}`);
    } else {
      // Reset to defaults for new template
      setTemplateName('New Template');
      setBackgroundColor('#FFFFFF');
      setSize('standard');
      setCustomWidth(400);
      setCustomHeight(600);
      setFrontFields([]);
      setBackFields([]);
      setSelectedField(null);
      setActiveSide('front');
    }
  }, [selectedTemplate]);

  const canvasConfig = size === 'custom'
    ? { width: customWidth, height: customHeight }
    : sizeConfigs[size];

  const currentFields = activeSide === 'front' ? frontFields : backFields;
  const setCurrentFields = activeSide === 'front' ? setFrontFields : setBackFields;

  // Snap to grid helper
  const snapToGrid = (value, gridSize = 10) => {
    return gridSnap ? Math.round(value / gridSize) * gridSize : value;
  };

  // Load preset template
  const loadPresetTemplate = (presetKey) => {
    const preset = presetTemplates[presetKey];
    setTemplateName(preset.name);
    setBackgroundColor(preset.backgroundColor);
    setSize('custom');
    setCustomWidth(preset.size.width);
    setCustomHeight(preset.size.height);
    setFrontFields(preset.fields.map(f => ({ ...f, id: `${f.type}_${Date.now()}_${Math.random()}` })));
    setBackFields([]);
    setSelectedField(null);
    setShowTemplateModal(false);
    message.success(`${preset.name} loaded!`);
  };



  // Drag & Drop handlers
  const handleDragStart = (e, fieldType) => {
    e.dataTransfer.setData('fieldType', fieldType);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    if (isPreviewMode) return;

    const fieldType = e.dataTransfer.getData('fieldType');
    const rect = e.currentTarget.getBoundingClientRect();
    const x = snapToGrid(e.clientX - rect.left - 50);
    const y = snapToGrid(e.clientY - rect.top - 15);

    const safeX = Math.max(0, Math.min(x, canvasConfig.width - 100));
    const safeY = Math.max(0, Math.min(y, canvasConfig.height - 30));

    if (fieldType) {
      const fieldConfig = fieldTypes.find(f => f.type === fieldType);
      const newField = {
        id: `${fieldType}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        type: fieldConfig.type,
        label: fieldConfig.label,
        properties: { ...fieldConfig.defaultProperties },
        position: { x: safeX, y: safeY },
        locked: false,
        visible: true,
        zIndex: currentFields.length
      };

      setCurrentFields(prev => [...prev, newField]);
      setSelectedField(newField);
      message.success(`${fieldConfig.label} added.`);
    }
  };

  // Field manipulation
  const updateFieldProperties = (fieldId, properties) => {
    setCurrentFields(prev =>
      prev.map(field =>
        field.id === fieldId
          ? { ...field, properties: { ...field.properties, ...properties } }
          : field
      )
    );

    if (selectedField?.id === fieldId) {
      setSelectedField(prev => ({
        ...prev,
        properties: { ...prev.properties, ...properties }
      }));
    }
  };

  const updateFieldPosition = (fieldId, position) => {
    setCurrentFields(prev =>
      prev.map(field =>
        field.id === fieldId
          ? { ...field, position: { ...field.position, ...position } }
          : field
      )
    );

    if (selectedField?.id === fieldId) {
      setSelectedField(prev => ({
        ...prev,
        position: { ...prev.position, ...position }
      }));
    }
  };

  const removeField = (fieldId) => {
    setCurrentFields(prev => prev.filter(field => field.id !== fieldId));
    if (selectedField?.id === fieldId) setSelectedField(null);
    message.success('Field removed.');
  };

  const toggleFieldLock = (fieldId) => {
    setCurrentFields(prev =>
      prev.map(field =>
        field.id === fieldId ? { ...field, locked: !field.locked } : field
      )
    );
  };

  const toggleFieldVisibility = (fieldId) => {
    setCurrentFields(prev =>
      prev.map(field =>
        field.id === fieldId ? { ...field, visible: !field.visible } : field
      )
    );
  };

  const duplicateField = (field) => {
    const newField = {
      ...field,
      id: `${field.type}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      position: { x: field.position.x + 20, y: field.position.y + 20 }
    };
    setCurrentFields(prev => [...prev, newField]);
    message.success('Field duplicated.');
  };

  const reorderFields = (fromIndex, toIndex) => {
    const newFields = [...currentFields];
    const [removed] = newFields.splice(fromIndex, 1);
    newFields.splice(toIndex, 0, removed);

    // Update zIndex based on new order
    const reindexed = newFields.map((field, index) => ({
      ...field,
      zIndex: index
    }));

    setCurrentFields(reindexed);
  };

  // Image upload handler
  const handleImageUpload = (file, fieldId) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      updateFieldProperties(fieldId, {
        imageData: e.target.result
      });
    };
    reader.readAsDataURL(file);
    return false; // Prevent upload
  };

  // Export template
  const exportTemplate = () => {
    const templateData = {
      name: templateName,
      size: canvasConfig,
      backgroundColor,
      frontFields,
      backFields
    };

    const dataStr = JSON.stringify(templateData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(dataBlob);
    link.download = `${templateName.replace(/\s+/g, '_')}_template.json`;
    link.click();
    message.info('Template exported successfully!');
  };

 const saveTemplate = async () => {
  // Validate required fields first
  if (!templateName.trim()) {
    message.error('Please enter a template name');
    return;
  }

  // console.log('=== SAVE TEMPLATE DEBUG ===');
  // console.log('selectedTemplate:', selectedTemplate);
  // console.log('selectedTemplate._id:', selectedTemplate?._id);

  const mappedFields = currentFields.map(f => ({
    type: f.type === 'qrcode' ? 'qr_code' : f.type,
    name: f.label,
    position: {
      x: f.position.x,
      y: f.position.y,
      width: f.properties.width || f.properties.size || 100,
      height: f.properties.height || f.properties.size || 30
    },
    style: {
      fontSize: f.properties.fontSize,
      fontWeight: f.properties.fontWeight,
      color: f.properties.color,
      alignment: f.properties.alignment || 'left'
    },
    content: f.properties.content || '',
    visible: f.visible,
    locked: f.locked,
    zIndex: f.zIndex
  }));

  const templateData = {
    name: templateName,
    description: `Custom badge template - ${activeSide} side`,
    organizationId: 'YOUR_ORG_ID', // TODO: Get from auth context
    createdBy: 'USER_ID', // TODO: Get from auth context
    backgroundColor,
    orientation: canvasConfig.width > canvasConfig.height ? 'landscape' : 'portrait',
    size: size,
    fields: mappedFields,
    metadata: {
      customDimensions: canvasConfig,
      frontFields: frontFields,
      backFields: backFields,
      activeSide
    }
  };

  // If editing existing template, include the ID
  if (selectedTemplate && selectedTemplate._id) {
    templateData._id = selectedTemplate._id;
  }

  // console.log('Final templateData to save:', templateData);
  // console.log('Request method:', templateData._id ? 'PUT' : 'POST');
  // console.log('=== END DEBUG ===');

  try {
    const { data } = await saveTicketTemplateApi(templateData);
    
    if (data.success) {
      const successMessage = selectedTemplate ? 'Template updated successfully!' : 'Template created successfully!';
      message.success(successMessage);
      console.log('Save response:', data);
      
      // Call onSave callback to notify parent
      if (onSave) {
        onSave(data.template);
      }
    } else {
      message.error(data.message || 'Failed to save template');
    }
  } catch (error) {
    console.error('Save template failed:', error);
    console.error('Error response:', error.response?.data);
    message.error(error.response?.data?.message || 'Something went wrong while saving');
  }
};
  // Render field preview
  const renderFieldPreview = (field) => {
    if (!field.visible) return null;

    const commonStyle = {
      pointerEvents: field.locked ? 'none' : 'auto',
      opacity: field.properties.opacity || 1
    };

    switch (field.type) {
      case 'text':
        return (
          <div style={{
            ...commonStyle,
            fontSize: field.properties.fontSize,
            fontWeight: field.properties.fontWeight,
            color: field.properties.color,
            textAlign: field.properties.alignment,
            userSelect: 'none'
          }}>
            {field.properties.content}
          </div>
        );

      case 'qrcode':
        return (
          <div style={{
            ...commonStyle,
            width: field.properties.size,
            height: field.properties.size,
            backgroundColor: field.properties.bgColor || '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 4,
            border: `2px solid ${field.properties.qrColor}`
          }}>
            <QrcodeOutlined style={{
              fontSize: field.properties.size * 0.5,
              color: field.properties.qrColor
            }} />
          </div>
        );

      case 'barcode':
        return (
          <div style={{
            ...commonStyle,
            width: field.properties.width,
            height: field.properties.height,
            backgroundColor: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '1px solid #ddd',
            fontFamily: 'monospace',
            fontSize: 10
          }}>
            <BarcodeOutlined style={{ fontSize: field.properties.height * 0.6 }} />
          </div>
        );

      case 'divider':
        return (
          <div style={{
            ...commonStyle,
            width: field.properties.width,
            height: field.properties.height,
            backgroundColor: field.properties.color,
            borderTop: `${field.properties.height}px ${field.properties.style} ${field.properties.color}`
          }} />
        );

      case 'image':
        return (
          <div style={{
            ...commonStyle,
            width: field.properties.width,
            height: field.properties.height,
            backgroundColor: '#f0f0f0',
            border: '2px dashed #d9d9d9',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden'
          }}>
            {field.properties.imageData ? (
              <img
                src={field.properties.imageData}
                alt={field.label}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: field.properties.objectFit
                }}
              />
            ) : (
              <PictureOutlined style={{ fontSize: 32, color: '#bfbfbf' }} />
            )}
          </div>
        );

      case 'datetime':
        return (
          <div style={{
            ...commonStyle,
            fontSize: field.properties.fontSize,
            color: field.properties.color,
            userSelect: 'none'
          }}>
            {field.properties.content}
          </div>
        );

      case 'container':
        return (
          <div style={{
            ...commonStyle,
            width: field.properties.width,
            height: field.properties.height,
            backgroundColor: field.properties.backgroundColor,
            borderRadius: field.properties.borderRadius,
            border: field.properties.borderStyle !== 'none'
              ? `${field.properties.borderWidth}px ${field.properties.borderStyle} ${field.properties.borderColor}`
              : 'none'
          }} />
        );

      default:
        return null;
    }
  };

  // Properties form handler
  const handlePropertiesFormChange = (changedValues) => {
    if (!selectedField) return;

    const { x, y, ...propChanges } = changedValues;

    if (x !== undefined || y !== undefined) {
      updateFieldPosition(selectedField.id, {
        ...(x !== undefined && { x: snapToGrid(x) }),
        ...(y !== undefined && { y: snapToGrid(y) })
      });
    }

    if (Object.keys(propChanges).length > 0) {
      updateFieldProperties(selectedField.id, propChanges);
    }
  };

  return (
    <Layout style={{ height: '100vh', fontFamily: 'Inter, sans-serif' }}>
      {/* Header */}
      <Header style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        background: '#fff',
        padding: '0 24px',
        borderBottom: '1px solid #f0f0f0',
        zIndex: 10
      }}>
        <Title level={4} style={{ margin: 0 }}>{templateName}</Title>
        <Space size="middle">
          <Button onClick={() => setShowTemplateModal(true)}>
            Load Template
          </Button>
          <Button
            icon={<EyeOutlined />}
            onClick={() => setIsPreviewMode(!isPreviewMode)}
            type={isPreviewMode ? 'primary' : 'default'}
          >
            {isPreviewMode ? 'Edit Mode' : 'Preview'}
          </Button>
          <Button icon={<DownloadOutlined />} onClick={exportTemplate}>
            Export
          </Button>
          <Button
            icon={<SaveOutlined />}
            type="primary"
            onClick={saveTemplate}
          >
            Save Template
          </Button>
        </Space>
      </Header>

      <Layout style={{ flexGrow: 1 }}>
        {/* Left Sidebar - Tools */}
        <Sider width={280} theme="light" style={{
          borderRight: '1px solid #f0f0f0',
          overflowY: 'auto',
          padding: '16px'
        }}>
          <Form layout="vertical" size="small">
            <Form.Item label={<Text strong>Template Name</Text>}>
              <Input
                value={templateName}
                onChange={e => setTemplateName(e.target.value)}
                placeholder="Enter template name"
              />
            </Form.Item>

            <Form.Item label={<Text strong>Background Color</Text>}>
              <ColorPicker
                value={backgroundColor}
                onChange={color => setBackgroundColor(color.toHexString())}
                showText
                style={{ width: '100%' }}
              />
            </Form.Item>

            <Form.Item label={<Text strong>Size Preset</Text>}>
              <Select value={size} onChange={setSize}>
                <Option value="standard">Standard (400x600)</Option>
                <Option value="badge">Badge (300x400)</Option>
                <Option value="landscape">Landscape (600x400)</Option>
                <Option value="a4-portrait">A4 Portrait</Option>
                <Option value="custom">Custom Size</Option>
              </Select>
            </Form.Item>

            {size === 'custom' && (
              <Space style={{ width: '100%' }}>
                <Form.Item label="Width" style={{ flex: 1 }}>
                  <InputNumber
                    min={100}
                    max={1000}
                    value={customWidth}
                    onChange={setCustomWidth}
                    style={{ width: '100%' }}
                  />
                </Form.Item>
                <Form.Item label="Height" style={{ flex: 1 }}>
                  <InputNumber
                    min={100}
                    max={1200}
                    value={customHeight}
                    onChange={setCustomHeight}
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Space>
            )}

            <Space style={{ width: '100%', marginBottom: 16 }}>
              <Text strong style={{ fontSize: 12 }}>Grid Snap:</Text>
              <Switch checked={gridSnap} onChange={setGridSnap} size="small" />
              <Text strong style={{ fontSize: 12, marginLeft: 8 }}>Show Grid:</Text>
              <Switch checked={showGrid} onChange={setShowGrid} size="small" />
            </Space>

            <Divider />

            <Text strong style={{ marginBottom: 12, display: 'block' }}>
              Add Fields (Drag & Drop)
            </Text>
            <Space direction="vertical" style={{ width: '100%' }} size="small">
              {fieldTypes.map((fieldType) => (
                <div
                  key={fieldType.type}
                  draggable={!isPreviewMode}
                  onDragStart={e => handleDragStart(e, fieldType.type)}
                  style={{
                    padding: 10,
                    border: '2px dashed #d9d9d9',
                    borderRadius: 6,
                    cursor: isPreviewMode ? 'not-allowed' : 'grab',
                    backgroundColor: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    opacity: isPreviewMode ? 0.5 : 1
                  }}
                >
                  {fieldType.icon}
                  <Text strong style={{ fontSize: 13 }}>{fieldType.label}</Text>
                </div>
              ))}
            </Space>

            <Divider />

            {/* Layer Management */}
            <Text strong style={{ marginBottom: 12, display: 'block', textTransform: 'uppercase', fontSize: 12 }}>
              Layers ({currentFields.length})
            </Text>
            <Space direction="vertical" style={{ width: '100%' }} size="small">
              {[...currentFields]
                .sort((a, b) => (b.zIndex || 0) - (a.zIndex || 0))
                .map((field, index) => (
                  <Card
                    key={field.id}
                    size="small"
                    onClick={() => !field.locked && setSelectedField(field)}
                    style={{
                      cursor: field.locked ? 'not-allowed' : 'pointer',
                      borderColor: selectedField?.id === field.id ? '#6366f1' : '#d9d9d9',
                      backgroundColor: selectedField?.id === field.id ? '#f0f5ff' : 'transparent',
                      opacity: field.visible ? 1 : 0.5
                    }}
                    bodyStyle={{
                      padding: 8,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}
                  >
                    <Space size="small">
                      <MenuOutlined style={{ fontSize: 12, color: '#8c8c8c' }} />
                      <Text strong style={{ fontSize: 12 }}>{field.label}</Text>
                    </Space>
                    <Space size="small">
                      <Tooltip title={field.visible ? 'Hide' : 'Show'}>
                        <Button
                          type="text"
                          size="small"
                          icon={field.visible ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                          onClick={e => { e.stopPropagation(); toggleFieldVisibility(field.id); }}
                        />
                      </Tooltip>
                      <Tooltip title={field.locked ? 'Unlock' : 'Lock'}>
                        <Button
                          type="text"
                          size="small"
                          icon={field.locked ? <LockOutlined /> : <UnlockOutlined />}
                          onClick={e => { e.stopPropagation(); toggleFieldLock(field.id); }}
                        />
                      </Tooltip>
                      <Tooltip title="Duplicate">
                        <Button
                          type="text"
                          size="small"
                          icon={<CopyOutlined />}
                          onClick={e => { e.stopPropagation(); duplicateField(field); }}
                        />
                      </Tooltip>
                      <Button
                        type="text"
                        danger
                        size="small"
                        icon={<DeleteOutlined />}
                        onClick={e => { e.stopPropagation(); removeField(field.id); }}
                      />
                    </Space>
                  </Card>
                ))}
            </Space>
          </Form>
        </Sider>

        {/* Center Canvas */}
        <Content style={{
          flex: 1,
          padding: 32,
          overflow: 'auto',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flexDirection: 'column',
          backgroundColor: '#f5f5f5',
          position: 'relative'
        }}>
          {/* Side Selector */}
          <div style={{ marginBottom: 16 }}>
            <Radio.Group
              value={activeSide}
              onChange={e => {
                setActiveSide(e.target.value);
                setSelectedField(null);
              }}
              buttonStyle="solid"
            >
              <Radio.Button value="front">Front Side</Radio.Button>
              <Radio.Button value="back">Back Side</Radio.Button>
            </Radio.Group>
          </div>

          {/* Canvas */}
          <div
            ref={canvasRef}
            onDragOver={isPreviewMode ? undefined : e => e.preventDefault()}
            onDrop={isPreviewMode ? undefined : handleDrop}
            style={{
              width: canvasConfig.width,
              height: canvasConfig.height,
              backgroundColor,
              borderRadius: 8,
              boxShadow: '0 8px 16px rgba(0,0,0,0.15)',
              position: 'relative',
              overflow: 'hidden',
              backgroundImage: showGrid
                ? 'linear-gradient(#e0e0e0 1px, transparent 1px), linear-gradient(90deg, #e0e0e0 1px, transparent 1px)'
                : 'none',
              backgroundSize: showGrid ? '20px 20px' : 'auto'
            }}
          >
            <div style={{ width: '100%', height: '100%', position: 'relative' }}>
              {currentFields
                .sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0))
                .map((field) => (
                  <div
                    key={field.id}
                    style={{
                      position: 'absolute',
                      left: field.position.x,
                      top: field.position.y,
                      cursor: field.locked ? 'not-allowed' : (isPreviewMode ? 'default' : 'move'),
                      padding: 4,
                      borderRadius: 4,
                      border: selectedField?.id === field.id && !isPreviewMode
                        ? '2px solid #6366f1'
                        : '2px solid transparent',
                      backgroundColor: selectedField?.id === field.id && !isPreviewMode
                        ? 'rgba(99, 102, 241, 0.1)'
                        : 'transparent',
                      zIndex: field.zIndex || 0,
                      display: field.visible ? 'block' : 'none'
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!isPreviewMode && !field.locked) {
                        setSelectedField(field);
                      }
                    }}
                    draggable={!isPreviewMode && !field.locked}
                    onDragStart={(e) => {
                      e.dataTransfer.setData('fieldId', field.id);
                    }}
                    onDragEnd={(e) => {
                      const rect = canvasRef.current.getBoundingClientRect();
                      const newX = snapToGrid(e.clientX - rect.left - field.position.x);
                      const newY = snapToGrid(e.clientY - rect.top - field.position.y);
                      updateFieldPosition(field.id, { x: newX, y: newY });
                    }}
                  >
                    {renderFieldPreview(field)}
                  </div>
                ))}
            </div>
          </div>

          <div style={{ marginTop: 16, textAlign: 'center', color: '#8c8c8c', fontSize: 14 }}>
            {isPreviewMode
              ? "Preview Mode - Click 'Edit Mode' to make changes"
              : "Drag fields to reposition. Click to select and configure."}
          </div>
        </Content>

        {/* Right Sidebar - Properties */}
        <Sider width={280} theme="light" style={{
          borderLeft: '1px solid #f0f0f0',
          overflowY: 'auto',
          padding: 16
        }}>
          {isPreviewMode ? (
            <div style={{ textAlign: 'center', paddingTop: 32 }}>
              <EyeOutlined style={{ fontSize: 48, color: '#d9d9d9', marginBottom: 16 }} />
              <Title level={5}>Preview Mode</Title>
              <Text type="secondary">
                You are currently previewing your badge design. Switch to edit mode to make changes.
              </Text>
            </div>
          ) : selectedField ? (
            <>
              <div style={{ marginBottom: 16 }}>
                <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                  <Title level={5} style={{ margin: 0 }}>Field Properties</Title>
                  <Tag color="#6366F1">{selectedField.label}</Tag>
                </Space>
              </div>

              <Form
                layout="vertical"
                size="small"
                key={selectedField.id}
                initialValues={{
                  ...selectedField.properties,
                  x: selectedField.position.x,
                  y: selectedField.position.y
                }}
                onValuesChange={handlePropertiesFormChange}
              >
                {/* Common Properties */}
                <Form.Item label="Label" name="content">
                  <Input placeholder="Field content" />
                </Form.Item>

                <Divider style={{ margin: '12px 0' }}>Position</Divider>
                <Space style={{ width: '100%' }}>
                  <Form.Item label="X" name="x" style={{ flex: 1, marginBottom: 0 }}>
                    <InputNumber
                      min={0}
                      max={canvasConfig.width - 10}
                      style={{ width: '100%' }}
                    />
                  </Form.Item>
                  <Form.Item label="Y" name="y" style={{ flex: 1, marginBottom: 0 }}>
                    <InputNumber
                      min={0}
                      max={canvasConfig.height - 10}
                      style={{ width: '100%' }}
                    />
                  </Form.Item>
                </Space>

                <Divider style={{ margin: '12px 0' }}>Style</Divider>

                {/* Text Field Properties */}
                {selectedField.type === 'text' && (
                  <>
                    <Form.Item label="Font Size" name="fontSize">
                      <InputNumber min={8} max={72} style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item label="Font Weight" name="fontWeight">
                      <Select>
                        <Option value="normal">Normal</Option>
                        <Option value="bold">Bold</Option>
                        <Option value="lighter">Light</Option>
                      </Select>
                    </Form.Item>
                    <Form.Item label="Text Color" name="color">
                      <ColorPicker showText style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item label="Alignment" name="alignment">
                      <Radio.Group>
                        <Radio.Button value="left">
                          <AlignLeftOutlined />
                        </Radio.Button>
                        <Radio.Button value="center">
                          <AlignCenterOutlined />
                        </Radio.Button>
                        <Radio.Button value="right">
                          <AlignRightOutlined />
                        </Radio.Button>
                      </Radio.Group>
                    </Form.Item>
                  </>
                )}

                {/* QR Code Properties */}
                {selectedField.type === 'qrcode' && (
                  <>
                    <Form.Item label="QR Code Size" name="size">
                      <InputNumber min={50} max={300} style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item label="QR Color" name="qrColor">
                      <ColorPicker showText style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item label="Background Color" name="bgColor">
                      <ColorPicker showText style={{ width: '100%' }} />
                    </Form.Item>
                  </>
                )}

                {/* Barcode Properties */}
                {selectedField.type === 'barcode' && (
                  <>
                    <Form.Item label="Width" name="width">
                      <InputNumber min={100} max={400} style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item label="Height" name="height">
                      <InputNumber min={30} max={150} style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item label="Barcode Color" name="color">
                      <ColorPicker showText style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item label="Format" name="format">
                      <Select>
                        <Option value="CODE128">CODE128</Option>
                        <Option value="CODE39">CODE39</Option>
                        <Option value="EAN13">EAN13</Option>
                        <Option value="UPC">UPC</Option>
                      </Select>
                    </Form.Item>
                  </>
                )}

                {/* Image Properties */}
                {selectedField.type === 'image' && (
                  <>
                    <Form.Item label="Upload Image">
                      <Upload
                        accept="image/*"
                        beforeUpload={(file) => handleImageUpload(file, selectedField.id)}
                        showUploadList={false}
                      >
                        <Button icon={<UploadOutlined />} style={{ width: '100%' }}>
                          Choose Image
                        </Button>
                      </Upload>
                    </Form.Item>
                    <Form.Item label="Width" name="width">
                      <InputNumber min={20} max={500} style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item label="Height" name="height">
                      <InputNumber min={20} max={500} style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item label="Object Fit" name="objectFit">
                      <Select>
                        <Option value="cover">Cover</Option>
                        <Option value="contain">Contain</Option>
                        <Option value="fill">Fill</Option>
                      </Select>
                    </Form.Item>
                    <Form.Item label="Opacity" name="opacity">
                      <InputNumber min={0} max={1} step={0.1} style={{ width: '100%' }} />
                    </Form.Item>
                  </>
                )}

                {/* Container Properties */}
                {selectedField.type === 'container' && (
                  <>
                    <Form.Item label="Width" name="width">
                      <InputNumber min={50} max={canvasConfig.width} style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item label="Height" name="height">
                      <InputNumber min={20} max={canvasConfig.height} style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item label="Background Color" name="backgroundColor">
                      <ColorPicker showText style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item label="Border Radius" name="borderRadius">
                      <InputNumber min={0} max={50} style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item label="Border Style" name="borderStyle">
                      <Select>
                        <Option value="none">None</Option>
                        <Option value="solid">Solid</Option>
                        <Option value="dashed">Dashed</Option>
                        <Option value="dotted">Dotted</Option>
                      </Select>
                    </Form.Item>
                    <Form.Item label="Border Color" name="borderColor">
                      <ColorPicker showText style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item label="Border Width" name="borderWidth">
                      <InputNumber min={0} max={20} style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item label="Opacity" name="opacity">
                      <InputNumber min={0} max={1} step={0.1} style={{ width: '100%' }} />
                    </Form.Item>
                  </>
                )}

                {/* Divider Properties */}
                {selectedField.type === 'divider' && (
                  <>
                    <Form.Item label="Width" name="width">
                      <InputNumber min={10} max={canvasConfig.width} style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item label="Thickness" name="height">
                      <InputNumber min={1} max={20} style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item label="Color" name="color">
                      <ColorPicker showText style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item label="Style" name="style">
                      <Select>
                        <Option value="solid">Solid</Option>
                        <Option value="dashed">Dashed</Option>
                        <Option value="dotted">Dotted</Option>
                      </Select>
                    </Form.Item>
                  </>
                )}

                {/* Date/Time Properties */}
                {selectedField.type === 'datetime' && (
                  <>
                    <Form.Item label="Font Size" name="fontSize">
                      <InputNumber min={8} max={72} style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item label="Text Color" name="color">
                      <ColorPicker showText style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item label="Format" name="format">
                      <Select>
                        <Option value="MMMM DD, YYYY">November 21, 2025</Option>
                        <Option value="MM/DD/YYYY">11/21/2025</Option>
                        <Option value="DD/MM/YYYY">21/11/2025</Option>
                        <Option value="YYYY-MM-DD">2025-11-21</Option>
                      </Select>
                    </Form.Item>
                  </>
                )}

                <Divider style={{ margin: '16px 0' }} />

                <Space direction="vertical" style={{ width: '100%' }} size="small">
                  <Button
                    icon={<CopyOutlined />}
                    onClick={() => duplicateField(selectedField)}
                    style={{ width: '100%' }}
                  >
                    Duplicate Field
                  </Button>
                  <Button
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => removeField(selectedField.id)}
                    style={{ width: '100%' }}
                  >
                    Remove Field
                  </Button>
                </Space>
              </Form>
            </>
          ) : (
            <>
              <Title level={5} style={{ marginBottom: 16 }}>Field Properties</Title>
              <Card size="small" style={{ backgroundColor: '#e6f7ff', borderColor: '#91d5ff' }}>
                <div style={{ fontSize: 12 }}>
                  <Text strong style={{ color: '#1890ff' }}>💡 Design Tips</Text>
                  <div style={{ marginTop: 8, color: '#595959', lineHeight: 1.6 }}>
                    • Drag fields from the left panel onto the canvas<br />
                    • Click any field to edit its properties<br />
                    • Use layers panel to manage visibility and order<br />
                    • Enable grid snap for precise alignment<br />
                    • Lock fields to prevent accidental changes
                  </div>
                </div>
              </Card>
            </>
          )}
        </Sider>
      </Layout>

      {/* Template Selection Modal */}
      <Modal
        title="Choose a Template"
        open={showTemplateModal}
        onCancel={() => setShowTemplateModal(false)}
        footer={null}
        width={700}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          {Object.entries(presetTemplates).map(([key, template]) => (
            <Card
              key={key}
              hoverable
              onClick={() => loadPresetTemplate(key)}
              style={{ cursor: 'pointer' }}
            >
              <Space direction="vertical" style={{ width: '100%' }}>
                <Title level={5} style={{ margin: 0 }}>{template.name}</Title>
                <Text type="secondary">
                  {template.size.width} x {template.size.height} px • {template.fields.length} fields
                </Text>
                <div style={{
                  width: '100%',
                  height: 100,
                  backgroundColor: template.backgroundColor,
                  borderRadius: 4,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Text style={{ color: '#fff', fontSize: 16, fontWeight: 'bold' }}>
                    Preview
                  </Text>
                </div>
              </Space>
            </Card>
          ))}
        </Space>
      </Modal>


    </Layout>
  );
};

export default TemplateDesigner;





// import React, { useState } from 'react';
// import {
//   Layout, Form, Input, Button, Select, ColorPicker, InputNumber,
//   Card, Divider, Space, Tag, message, Typography
// } from 'antd';
// import {
//   EyeOutlined, SaveOutlined, DeleteOutlined, PictureOutlined,
//   QrcodeOutlined, FontSizeOutlined, DownloadOutlined,
//   BarcodeOutlined,
//   BorderOutlined,
//   CalendarOutlined,
//   MinusOutlined
// } from '@ant-design/icons';
// import { saveTicketTemplateApi } from '../../../redux/api/api';

// const { Header, Content } = Layout;
// const { Option } = Select;
// const { Title, Text } = Typography;

// const fieldTypes = [
//   { type: 'text', label: 'Text Field', icon: <FontSizeOutlined style={{ fontSize: 18 }} />, defaultProperties: { fontSize: 16, fontWeight: 'normal', color: '#FFFFFF', content: 'Sample Text' } },
//   { type: 'qrcode', label: 'QR Code', icon: <QrcodeOutlined style={{ fontSize: 18 }} />, defaultProperties: { size: 250, content: 'QR_CODE_DATA', qrColor: '#000000' } }, // Increased default size
//   { type: 'barcode', label: 'Barcode', icon: <BarcodeOutlined style={{ fontSize: 18 }} />, defaultProperties: { width: 200, height: 50, content: 'BARCODE12345', color: '#000000' } },
//   { type: 'divider', label: 'Divider Line', icon: <MinusOutlined style={{ fontSize: 18 }} />, defaultProperties: { width: '90%', height: 2, color: '#FFFFFF', style: 'dotted', zIndex: 1 } },
//   { type: 'image', label: 'Image/Logo', icon: <PictureOutlined style={{ fontSize: 18 }} />, defaultProperties: { width: 80, height: 40, content: 'LOGO_PLACEHOLDER' } },
//   { type: 'datetime', label: 'Date/Time', icon: <CalendarOutlined style={{ fontSize: 18 }} />, defaultProperties: { fontSize: 16, format: 'MMMM DD, YYYY', content: 'October 15, 2025', color: '#FFFFFF' } },
//   // --- NEW CONTAINER FIELD ---
//   {
//     type: 'container',
//     label: 'Design Box/Section',
//     icon: <BorderOutlined style={{ fontSize: 18 }} />,
//     defaultProperties: {
//       width: 350,
//       height: 100,
//       backgroundColor: '#990033', // Maroon color
//       borderRadius: 0,
//       borderStyle: 'none',
//       borderColor: 'transparent',
//       borderWidth: 0,
//       zIndex: 0 // Containers should be behind text
//     }
//   }
//   // ---------------------------
// ];

// const defaultTemplate = {
//   name: 'Default Event Ticket',
//   backgroundColor: '#4B6CB7',
//   size: { width: 400, height: 500 }, // Default start size
//   fields: [
//     {
//       id: 'text_1',
//       type: 'text',
//       label: 'Event Name',
//       properties: { fontSize: 20, color: '#FFFFFF', content: 'Tech Expo 2025', fontWeight: 'bold' },
//       position: { x: 100, y: 60 }
//     },
//     {
//       id: 'text_2',
//       type: 'text',
//       label: 'Attendee Name',
//       properties: { fontSize: 16, color: '#FFFFFF', content: 'Jane Smith' },
//       position: { x: 130, y: 260 }
//     },
//     {
//       id: 'qrcode_1',
//       type: 'qrcode',
//       label: 'QR Code',
//       properties: { size: 100, content: 'SAMPLE_QR_DATA_1234' },
//       position: { x: 130, y: 130 }
//     }
//   ]
// };

// // Size presets
// const sizeConfigs = {
//   standard: { width: 400, height: 600 },
//   badge: { width: 300, height: 400 },
//   landscape: { width: 600, height: 400 },
//   custom: null // Will use user input
// };

// const TemplateDesigner = () => {
//   const [templateName, setTemplateName] = useState(defaultTemplate.name);
//   const [selectedColor, setSelectedColor] = useState(defaultTemplate.backgroundColor);
//   const [templateFields, setTemplateFields] = useState(defaultTemplate.fields);
//   const [selectedField, setSelectedField] = useState(null);
//   const [isPreviewMode, setIsPreviewMode] = useState(false);
//   const [size, setSize] = useState('standard');
//   const [customWidth, setCustomWidth] = useState(400);
//   const [customHeight, setCustomHeight] = useState(600);

//   // Use either preset or custom size
//   const canvasConfig = sizeConfigs[size] || sizeConfigs['standard'];


//   // Reset the template including current sizes
//   const resetToDefault = () => {
//     setTemplateName(defaultTemplate.name);
//     setSelectedColor(defaultTemplate.backgroundColor);
//     setTemplateFields(defaultTemplate.fields);
//     setSelectedField(null);
//     setSize('standard');
//     setCustomWidth(400);
//     setCustomHeight(600);
//     message.success('Default template restored.');
//   };

//   // Drag & Drop logic
//   const handleDragStart = (e, fieldType) => {
//     e.dataTransfer.setData('fieldType', fieldType);
//   };
//   const handleDrop = (e) => {
//     e.preventDefault();
//     const fieldType = e.dataTransfer.getData('fieldType');
//     const rect = e.currentTarget.getBoundingClientRect();
//     const x = e.clientX - rect.left - 50;
//     const y = e.clientY - rect.top - 15;
//     const safeX = Math.max(0, Math.min(x, canvasConfig.width - 100));
//     const safeY = Math.max(0, Math.min(y, canvasConfig.height - 30));
//     if (fieldType) {
//       const fieldConfig = fieldTypes.find(f => f.type === fieldType);
//       const newField = {
//         id: `field_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
//         type: fieldConfig.type,
//         label: fieldConfig.label,
//         properties: { ...fieldConfig.defaultProperties },
//         position: { x: safeX, y: safeY }
//       };
//       setTemplateFields(prev => [...prev, newField]);
//       setSelectedField(newField);
//       message.success(`${fieldConfig.label} added.`);
//     }
//   };

//   const updateFieldProperties = (fieldId, properties) => {
//     setTemplateFields(prev =>
//       prev.map(field =>
//         field.id === fieldId
//           ? { ...field, properties: { ...field.properties, ...properties } }
//           : field
//       )
//     );
//     setSelectedField(prev =>
//       prev && prev.id === fieldId
//         ? { ...prev, properties: { ...prev.properties, ...properties } }
//         : prev
//     );
//   };
//   const removeField = (fieldId) => {
//     setTemplateFields(prev => prev.filter(field => field.id !== fieldId));
//     if (selectedField?.id === fieldId) setSelectedField(null);
//     message.success('Field removed.');
//   };
//   const exportTemplate = () => {
//     const templateData = {
//       name: templateName,
//       size: canvasConfig,
//       backgroundColor: selectedColor,
//       fields: templateFields
//     };
//     const dataStr = JSON.stringify(templateData, null, 2);
//     const dataBlob = new Blob([dataStr], { type: 'application/json' });
//     const link = document.createElement('a');
//     link.href = URL.createObjectURL(dataBlob);
//     link.download = `${templateName.replace(/\s+/g, '_')}_template.json`;
//     link.click();
//     message.info('Template exported successfully!');
//   };
//   const renderFieldPreview = (field) => {
//     switch (field.type) {
//       case 'text':
//         return (
//           <div style={{
//             fontSize: field.properties.fontSize,
//             fontWeight: field.properties.fontWeight,
//             color: field.properties.color
//           }}>
//             {field.properties.content}
//           </div>
//         );
//       case 'qrcode':
//         return (
//           <div style={{
//             width: field.properties.size, height: field.properties.size,
//             backgroundColor: '#fff', display: 'flex', alignItems: 'center',
//             justifyContent: 'center', borderRadius: 4
//           }}>
//             <QrcodeOutlined style={{ fontSize: field.properties.size * 0.5, color: '#666' }} />
//           </div>
//         );
//       default:
//         return null;
//     }
//   };
//   const initialFieldValues = selectedField
//     ? { ...selectedField.properties, x: selectedField.position.x, y: selectedField.position.y }
//     : {};
//   const handlePropertiesFormChange = (changedValues, allValues) => {
//     if (!selectedField) return;
//     const { x, y, ...propChanges } = changedValues;
//     if (x !== undefined || y !== undefined) {
//       setTemplateFields(prev =>
//         prev.map(field =>
//           field.id === selectedField.id
//             ? { ...field, position: { x: allValues.x, y: allValues.y } }
//             : field
//         )
//       );
//       setSelectedField(prev => prev ? { ...prev, position: { x: allValues.x, y: allValues.y } } : prev);
//     }
//     if (Object.keys(propChanges).length > 0) updateFieldProperties(selectedField.id, propChanges);
//   };


//   const saveTemplate = async () => {
//     const mappedFields = templateFields.map(f => ({
//       type: f.type === 'qrcode' ? 'qr_code' : f.type,
//       name: f.label,
//       position: {
//         x: f.position.x,
//         y: f.position.y,
//         width: f.properties.width || f.properties.size || 100,
//         height: f.properties.height || f.properties.size || 30
//       },
//       style: {
//         fontSize: f.properties.fontSize,
//         fontWeight: f.properties.fontWeight,
//         color: f.properties.color,
//         alignment: 'left'
//       },
//       required: f.required || false
//     }));

//     const templateData = {
//       name: templateName,
//       size: canvasConfig,
//       backgroundColor: selectedColor,
//       fields: mappedFields,
//       // defaultDesign,
//       // organizationId: selectedOrganizationId,
//       // createdBy: currentUserId
//     };

//     console.log(templateData)

//     // return

//     try {
//       const { data } = await saveTicketTemplateApi(templateData);
//       // console.log
//       // const data = await res.json();
//       if (data.success) {
//         message.success('Template saved successfully!');
//       } else {
//         message.error(data.message || 'Failed to save template');
//       }
//     } catch (error) {
//       console.error('Save template failed:', error);
//       message.error('Something went wrong while saving');
//     }
//   };

//   return (
//     <Layout style={{ height: '80vh', display: 'flex', flexDirection: 'column', fontFamily: 'Inter, sans-serif' }}>
//       <Header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#fff', padding: '0 24px', borderBottom: '1px solid #f0f0f0' }}>
//         <Title level={4} style={{ margin: 0 }}>{templateName}</Title>
//         <Space size="middle">
//           <Button icon={<EyeOutlined />} onClick={() => setIsPreviewMode(!isPreviewMode)} type={isPreviewMode ? 'primary' : 'default'}>
//             {isPreviewMode ? 'Edit Mode' : 'Preview'}
//           </Button>
//           <Button icon={<DownloadOutlined />} onClick={exportTemplate}>Export</Button>
//           <Button icon={<SaveOutlined />} type="primary" style={{ backgroundColor: '#6366F1', borderColor: '#6366F1' }} onClick={saveTemplate}>Save Template</Button>
//           <Button icon={<DeleteOutlined />} onClick={resetToDefault}>Reset to Default</Button>
//         </Space>
//       </Header>
//       <Layout hasSider style={{ flexGrow: 1 }}>
//         <div style={{ width: 288, background: '#fff', padding: 24, borderRight: '1px solid #f0f0f0', overflowY: 'auto' }}>
//           <Form layout="vertical" size="small">
//             <Form.Item label={<Text strong>Template Name</Text>} required>
//               <Input value={templateName} onChange={e => setTemplateName(e.target.value)} placeholder="Enter template name" />
//             </Form.Item>
//             <Form.Item label={<Text strong>Background Color</Text>}>
//               <ColorPicker value={selectedColor} onChange={color => setSelectedColor(color.toHexString())} showText style={{ width: '100%' }} />
//             </Form.Item>
//             <Form.Item label={<Text strong>Size</Text>}>
//               <Select value={size} onChange={setSize}>
//                 <Option value="standard">Standard 4x6</Option>
//                 <Option value="badge">Badge 3x4</Option>
//                 <Option value="landscape">Landscape 6x4</Option>
//                 <Option value="custom">Custom</Option>
//               </Select>
//             </Form.Item>
//             {size === 'custom' && (
//               <div style={{ display: 'flex', gap: 12 }}>
//                 <Form.Item label="Width" style={{ flex: 1 }}>
//                   <InputNumber min={100} max={1000} value={customWidth} onChange={setCustomWidth} style={{ width: '100%' }} />
//                 </Form.Item>
//                 <Form.Item label="Height" style={{ flex: 1 }}>
//                   <InputNumber min={100} max={1200} value={customHeight} onChange={setCustomHeight} style={{ width: '100%' }} />
//                 </Form.Item>
//               </div>
//             )}
//             <Divider />
//             <Text strong style={{ marginBottom: 12, display: 'block' }}>Add Fields (Drag & Drop)</Text>
//             <Space direction="vertical" style={{ width: '100%' }}>
//               {fieldTypes.map((fieldType) => (
//                 <div
//                   key={fieldType.type}
//                   draggable
//                   onDragStart={e => handleDragStart(e, fieldType.type)}
//                   style={{
//                     padding: 12, border: '2px dashed #d9d9d9', borderRadius: 6, cursor: 'grab',
//                     backgroundColor: 'white', display: 'flex', alignItems: 'center', gap: 12,
//                   }}
//                 >
//                   {fieldType.icon}
//                   <Text strong>{fieldType.label}</Text>
//                 </div>
//               ))}
//             </Space>
//             <Divider />
//             <Text type="secondary" strong style={{ marginBottom: 12, display: 'block', textTransform: 'uppercase' }}>Current Fields</Text>
//             <Space direction="vertical" style={{ width: '100%' }}>
//               {templateFields.map((field) => (
//                 <Card
//                   key={field.id}
//                   size="small"
//                   onClick={() => setSelectedField(field)}
//                   style={{
//                     cursor: 'pointer',
//                     borderColor: selectedField?.id === field.id ? '#6366f1' : '#d9d9d9',
//                     backgroundColor: selectedField?.id === field.id ? '#f0f5ff' : 'transparent',
//                   }}
//                   bodyStyle={{ padding: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
//                 >
//                   <Text strong>{field.label}</Text>
//                   <Button type="text" danger size="small" icon={<DeleteOutlined />} onClick={e => { e.stopPropagation(); removeField(field.id); }} />
//                 </Card>
//               ))}
//             </Space>
//           </Form>
//         </div>
//         <Content style={{ flex: 1, padding: 32, overflow: 'auto', display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column', backgroundColor: '#f5f5f5' }}>
//           <div
//             onDragOver={isPreviewMode ? undefined : e => e.preventDefault()}
//             onDrop={isPreviewMode ? undefined : handleDrop}
//             style={{
//               width: canvasConfig.width, height: canvasConfig.height,
//               backgroundColor: selectedColor, borderRadius: 8,
//               boxShadow: '0 8px 16px rgba(0,0,0,0.15)', position: 'relative', overflow: 'hidden'
//             }}
//           >
//             <div style={{ width: '100%', height: '100%', position: 'relative' }}>
//               {templateFields.map((field) => (
//                 <div
//                   key={field.id}
//                   style={{
//                     position: 'absolute',
//                     left: field.position.x,
//                     top: field.position.y,
//                     minWidth: field.type === 'qrcode' ? field.properties.size : 'auto',
//                     minHeight: field.type === 'qrcode' ? field.properties.size : 30,
//                     padding: 8,
//                     borderRadius: 4,
//                     border: selectedField?.id === field.id ? '2px solid #6366f1' : '2px solid rgba(255,255,255,0.3)',
//                     backgroundColor: selectedField?.id === field.id ? 'rgba(99, 102, 241, 0.1)' : 'transparent',
//                     zIndex: selectedField?.id === field.id ? 10 : 1, display: 'inline-block'
//                   }}
//                   onClick={() => setSelectedField(field)}
//                 >
//                   {renderFieldPreview(field)}
//                 </div>
//               ))}
//             </div>
//           </div>
//           <div style={{ marginTop: 16, textAlign: 'center', color: '#8c8c8c', fontSize: 14 }}>
//             {isPreviewMode
//               ? "Preview Mode - Click 'Edit Mode' to make changes"
//               : "Drag fields to reposition. Click to select and configure."}
//           </div>
//         </Content>
//         <div style={{ width: 288, background: 'white', borderLeft: '1px solid #f0f0f0', padding: 24, overflowY: 'auto' }}>
//           {isPreviewMode ? (
//             <div style={{ textAlign: 'center', paddingTop: 32 }}>
//               <EyeOutlined style={{ fontSize: 48, color: '#d9d9d9', marginBottom: 16 }} />
//               <Title level={5}>Preview Mode</Title>
//               <Text type="secondary">You are currently previewing your ticket design. Switch to edit mode to make changes.</Text>
//             </div>
//           ) : selectedField ? (
//             <>
//               <div style={{ marginBottom: 16 }}>
//                 <Space style={{ width: '100%', justifyContent: 'space-between' }}>
//                   <Title level={5} style={{ margin: 0 }}>Field Properties</Title>
//                   <Tag color="#6366F1">{selectedField.label}</Tag>
//                 </Space>
//               </div>
//               <Form
//                 layout="vertical"
//                 size="small"
//                 key={selectedField.id}
//                 initialValues={initialFieldValues}
//                 onValuesChange={handlePropertiesFormChange}
//               >
//                 <Form.Item label="Content" name="content"><Input /></Form.Item>
//                 <Divider style={{ margin: '16px 0 8px' }} orientation="left" plain>Position (px)</Divider>
//                 <Space>
//                   <Form.Item label="X" name="x" style={{ flex: 1, marginBottom: 0 }}>
//                     <InputNumber min={0} max={canvasConfig.width - 10} />
//                   </Form.Item>
//                   <Form.Item label="Y" name="y" style={{ flex: 1, marginBottom: 0 }}>
//                     <InputNumber min={0} max={canvasConfig.height - 10} />
//                   </Form.Item>
//                 </Space>
//                 <Divider style={{ margin: '16px 0 8px' }} orientation="left" plain>Style</Divider>
//                 {selectedField.type === 'text' && (
//                   <>
//                     <Form.Item label="Font Size" name="fontSize">
//                       <InputNumber min={8} max={72} style={{ width: '100%' }} />
//                     </Form.Item>
//                     <Form.Item label="Text Color" name="color">
//                       <ColorPicker showText style={{ width: '100%' }} />
//                     </Form.Item>
//                   </>
//                 )}
//                 {selectedField.type === 'qrcode' && (
//                   <Form.Item label="QR Code Size" name="size">
//                     <InputNumber min={50} max={200} style={{ width: '100%' }} />
//                   </Form.Item>
//                 )}
//                 <Divider style={{ margin: '16px 0' }} />
//                 <Button
//                   danger
//                   icon={<DeleteOutlined />}
//                   onClick={() => removeField(selectedField.id)}
//                   style={{ width: '100%' }}
//                 >Remove Field</Button>
//               </Form>
//             </>
//           ) : (
//             <>
//               <Title level={5} style={{ marginBottom: 16 }}>Field Properties</Title>
//               <Card size="small" style={{ backgroundColor: '#e6f7ff', borderColor: '#91d5ff' }}>
//                 <div style={{ fontSize: 12 }}>
//                   <Text strong style={{ color: '#1890ff' }}>💡 Design Tips</Text>
//                   <div style={{ marginTop: 8, color: '#595959' }}>
//                     {"Click on any existing field on the ticket to edit its properties."}
//                   </div>
//                 </div>
//               </Card>
//             </>
//           )}
//         </div>
//       </Layout>
//     </Layout>
//   );
// };

// export default TemplateDesigner;

